import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CreateInvitationBody } from '@tasks/contracts';

import { createInvitation } from '../../api/invitations';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select, type SelectOption } from '../../components/Select';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import type { GlobalRole } from '../../lib/types';
import { globalRoleLabels } from '../../i18n/enums';
import styles from './AdminPages.module.css';

const ROLE_OPTIONS: readonly SelectOption<GlobalRole>[] = (['admin', 'member'] as const).map(
  (value) => ({
    value,
    label: globalRoleLabels[value],
  }),
);

type FormState = {
  readonly displayName: string;
  readonly email: string;
  readonly role: GlobalRole;
};

const EMPTY_FORM: FormState = {
  displayName: '',
  email: '',
  role: 'member',
};

export interface InviteUserModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * UC-USR-06: приглашение обычного пользователя. Пароль он задаёт сам по
 * ссылке из письма; создание AI-агента живёт на `/agents` (UC-USR-08).
 */
export function InviteUserModal({ open, onClose }: InviteUserModalProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateInvitationBody) => createInvitation(body),
    onSuccess: (created) => {
      setForm(EMPTY_FORM);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.invitations });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      toast.success(`Приглашение отправлено на ${created.email}`);
      onClose();
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
      toast.error(human.message, { code: human.code });
    },
  });

  const submit = (): void => {
    setError(null);
    if (form.displayName.trim().length === 0) {
      setError('Введите имя');
      return;
    }
    if (form.email.trim().length === 0) {
      setError('Введите email');
      return;
    }
    mutation.mutate({
      email: form.email.trim(),
      displayName: form.displayName.trim(),
      role: form.role,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Пригласить пользователя"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={submit}>
            Отправить приглашение
          </Button>
        </>
      }
    >
      <div className={styles['form']}>
        <Input
          label="Имя"
          value={form.displayName}
          onChange={(event) => setForm({ ...form, displayName: event.target.value })}
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          hint="На этот адрес придёт ссылка для регистрации"
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />
        <Select
          label="Глобальная роль"
          options={ROLE_OPTIONS}
          value={form.role}
          onChange={(value) => setForm({ ...form, role: value })}
        />
        {error ? (
          <p className={styles['formError']} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
